'use client'

/* eslint-disable react-hooks/refs, react-hooks/set-state-in-effect */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PlayIcon, XIcon } from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'framer-motion'

import { VideoPlayer, VideoPlayerRef } from '@/components/common/player'
import { Button } from '@/components/ui/button'
import { useTaskRatio } from '@/hooks/utils'
import { useRouter } from '@/i18n/navigation'
import { CreationDetail } from '@/lib/api'
import { useCreationDetail } from '@/lib/api/hooks/creation/use-creation-detail'

import { UserAvatar } from '../user-avatar'
import { WorkProvider } from '../work-context'

import { WorkActionSide } from './work-action-side'

const RENDER_WINDOW_SIZE = 1

export type LegacyMobileWorkProps = {
  list: CreationDetail[]
  initialIndex: number
}

/**
 * 低版本设备专用的视频卡片
 */
const LegacyVideoCard = ({
  data: listData,
  isActive
}: {
  data: CreationDetail
  isActive: boolean
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<VideoPlayerRef>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [srcReady, setSrcReady] = useState(false)
  const [wantsPlay, setWantsPlay] = useState(false)
  
  const { data: apiData } = useCreationDetail(listData.jobId, { initialData: listData })
  
  const data = useMemo(() => {
    const baseData = apiData ?? listData
    return {
      ...baseData,
      thumbnail: listData.thumbnail || baseData.thumbnail,
      videoUrl: listData.videoUrl || baseData.videoUrl,
      cover: baseData.cover
    }
  }, [apiData, listData])

  const aspectRatio = useTaskRatio({
    aspectRatio: data.extra.aspectRatioV2,
    orientation: data.extra.aspectRatio
  })

  // 切换视频时重置状态
  useEffect(() => {
    setIsPaused(false)
    setIsPlaying(false)
    setSrcReady(false)
    setWantsPlay(false)
  }, [isActive])

  // 监听视频播放状态，作为兜底
  useEffect(() => {
    const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('playing', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('playing', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [isActive])

  // 兜底：暂停图标出现时强制停止播放
  useEffect(() => {
    if (isPaused && isPlaying) {
      const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null
      if (video && !video.paused) {
        video.pause()
      }
    }
  }, [isPaused, isPlaying])

  // 播放控制：首屏封面，点按后再播
  useEffect(() => {
    if (!isActive || !srcReady || !wantsPlay || isPaused) {
      if (!isActive) playerRef.current?.pause()
      return
    }

    let retryTimer: NodeJS.Timeout | null = null
    const tryPlay = () => {
      const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null
      if (!video) {
        retryTimer = setTimeout(tryPlay, 100)
        return
      }
      video.playsInline = true
      video.play()?.catch?.(() => {})
    }
    retryTimer = setTimeout(tryPlay, 50)

    return () => {
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [isActive, srcReady, wantsPlay, isPaused])

  return (
    <WorkProvider value={{ data, setSkipReset: () => {} }}>
      <div 
        ref={containerRef}
        className="relative flex h-full w-full flex-col bg-primary"
      >
        <div 
          className="relative flex flex-1 items-center justify-center overflow-hidden rounded-b-2xl"
          onClick={() => {
            if (!srcReady) {
              setSrcReady(true)
              setWantsPlay(true)
              setIsPaused(false)
              return
            }
            const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null
            if (video) {
              if (video.paused) {
                video.play()?.catch?.(() => {})
                setIsPaused(false)
                setWantsPlay(true)
              } else {
                video.pause()
                setIsPaused(true)
                setWantsPlay(false)
              }
            }
          }}
        >
          <VideoPlayer
            key={`video-${listData.id}`}
            ref={playerRef}
            aspectRatio={aspectRatio}
            className="flex size-full items-center justify-center"
            controls={false}
            fit="fill"
            gestures={false}
            load="idle"
            playButton={false}
            poster={data.thumbnail}
            src={isActive && srcReady ? data.videoUrl : undefined}
            classNames={{ 
              video: 'object-contain h-full', 
              container: 'relative flex-1 items-center justify-center w-full flex' 
            }}
            loading={{
              showOnInitial: true,
              showOnBuffering: true
            }}
            playback={{
              loop: true,
              autoPlay: false,
              muted: false
            }}
          />
          {/* 暂停状态播放图标 - 兜底：播放中强制隐藏 */}
          <AnimatePresence>
            {!isPlaying && (
              <motion.div
                key="play-icon"
                animate={{ opacity: 1 }}
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-black/50">
                  <PlayIcon color="white" size={24} weight="fill" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="absolute bottom-9 z-20 w-full">
            <div className="flex items-end">
              <div className="flex flex-1 flex-col gap-2.5 truncate px-4">
                <UserAvatar />
                <div className="flex flex-col truncate">
                  <span className="truncate text-sm font-semibold text-white">
                    {data.nickName}
                  </span>
                </div>
              </div>
              <WorkActionSide />
            </div>
          </div>
        </div>
      </div>
    </WorkProvider>
  )
}

/**
 * 低版本设备兼容的移动端视频滚动组件
 * 使用 transform 实现翻页效果（类似抖音）
 */
export const LegacyMobileWork = ({ list, initialIndex }: LegacyMobileWorkProps) => {
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [offset, setOffset] = useState(0) // 当前拖拽偏移
  const [isAnimating, setIsAnimating] = useState(false)
  
  const touchStartY = useRef(0)
  const touchStartTime = useRef(0)
  const isDragging = useRef(false)
  const lastTouchY = useRef(0)
  const animationDuration = useRef(300)
  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800

  // 更新 URL
  const updateUrl = useCallback((index: number) => {
    const currentVideo = list[index]
    if (currentVideo) {
      const videoId = currentVideo.jobId || currentVideo.id.toString()
      const locale = window.location.pathname.split('/')[1]
      const newUrl = `/${locale}/work/${videoId}`
      window.history.replaceState(
        { ...window.history.state, as: newUrl, url: newUrl },
        '',
        newUrl
      )
    }
  }, [list])

  // 触摸开始
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isAnimating) return
    const touchY = e.touches[0].clientY
    touchStartY.current = touchY
    lastTouchY.current = touchY
    touchStartTime.current = Date.now()
    isDragging.current = true
  }, [isAnimating])

  // 触摸移动 - 跟手拖拽
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || isAnimating) return

    const touchY = e.touches[0].clientY
    lastTouchY.current = touchY
    const deltaY = touchY - touchStartY.current

    // 放大滑动幅度，让拖动更轻松
    const amplifiedDelta = deltaY * 1.5

    // 边界阻尼（更柔和）
    let limitedDelta = amplifiedDelta
    if (currentIndex === 0 && amplifiedDelta > 0) {
      limitedDelta = Math.pow(amplifiedDelta, 0.7)
    }
    if (currentIndex === list.length - 1 && amplifiedDelta < 0) {
      limitedDelta = -Math.pow(Math.abs(amplifiedDelta), 0.7)
    }

    setOffset(limitedDelta)
  }, [isAnimating, currentIndex, list.length])

  // 触摸结束 - 判断切换方向，执行动画
  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current || isAnimating) return
    isDragging.current = false

    // 计算滑动时间
    const touchDuration = Date.now() - touchStartTime.current

    // 需要滑动屏幕高度的 25%（offset 被放大了 1.5 倍，所以实际阈值也要放大）
    const distanceThreshold = screenHeight * 0.25 * 1.5
    // 快速滑动：时间 < 400ms 且滑动距离 > 15px（放大后）
    const isQuickSwipe = touchDuration < 400 && Math.abs(offset) > 15

    let targetIndex = currentIndex
    let targetOffset = 0
    let willChange = false

    const shouldSwipeUp = offset < 0 && (Math.abs(offset) > distanceThreshold || isQuickSwipe)
    const shouldSwipeDown = offset > 0 && (offset > distanceThreshold || isQuickSwipe)

    if (shouldSwipeUp && currentIndex < list.length - 1) {
      targetIndex = currentIndex + 1
      targetOffset = -screenHeight
      willChange = true
    } else if (shouldSwipeDown && currentIndex > 0) {
      targetIndex = currentIndex - 1
      targetOffset = screenHeight
      willChange = true
    }

    // 动画时长固定 350ms，让用户看清切换
    animationDuration.current = 350

    // 开始动画
    setIsAnimating(true)
    setOffset(targetOffset)

    // 动画结束后更新状态
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current)
    }

    animationTimerRef.current = setTimeout(() => {
      setIsAnimating(false)
      setOffset(0)
      if (willChange) {
        setCurrentIndex(targetIndex)
        updateUrl(targetIndex)
      }
      animationTimerRef.current = null
    }, 350)
  }, [offset, currentIndex, list.length, screenHeight, isAnimating, updateUrl])

  const handleClose = useCallback(() => {
    router.back()
  }, [router])

  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current)
      }
    }
  }, [])

  // 计算总偏移：基于当前索引的位置 + 拖拽偏移
  const totalOffset = -currentIndex * screenHeight + offset

  return (
    <div 
      className="fixed inset-0 bg-black overflow-hidden"
      style={{ touchAction: 'none' }}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
    >
      {/* 关闭按钮 */}
      <div className="absolute top-4 left-4 z-30">
        <Button
          className="bg-glass h-10 w-10 rounded-full p-0"
          size="icon"
          variant="ghost"
          onClick={handleClose}
        >
          <XIcon className="text-white" size={24} />
        </Button>
      </div>

      {/* 页面容器 - 所有页面垂直排列 */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          transform: `translate3d(0, ${totalOffset}px, 0)`,
          transition: isAnimating
            ? `transform ${animationDuration.current}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
            : 'none',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000
        }}
      >
        {list.map((item, index) => (
          <div
            key={item.id}
            style={{
              height: screenHeight,
              width: '100%'
            }}
          >
            {Math.abs(index - currentIndex) <= RENDER_WINDOW_SIZE ? (
              <LegacyVideoCard
                data={item}
                isActive={
                  index === currentIndex ||
                  (isAnimating && offset < 0 && index === currentIndex + 1) ||
                  (isAnimating && offset > 0 && index === currentIndex - 1)
                }
              />
            ) : (
              <div className="h-full w-full bg-black" />
            )}
          </div>
        ))}
      </div>

      {/* 页码指示器 */}
      <div className="absolute bottom-4 right-4 z-30 bg-black/50 px-2 py-1 rounded text-white text-xs">
        {currentIndex + 1} / {list.length}
      </div>
    </div>
  )
}
